import { orderBy } from 'lodash';
import { CATEGORY_TYPE } from '@fiverr-private/listing_lib';
import { ALL_SUBCATEGORY } from '../../../../../../../lib/utils/side_filters/constants';
import { OPTION_PILLS_COUNT } from '../FilterRow.constants';
import type { ExposedFiltersType } from '../../../side_filters/types';
import type { CategoryExposure, CategoryOption, SubCategoryFilter, VisualFiltersData } from '../FilterRow.types';
import { debugCategoryExposure } from './debugCategoryExposure';

interface ResolveCategoryExposureParams {
    subCategory?: SubCategoryFilter;
    dominantLeafCategory?: string;
    dominantLeafCategoryParent?: string;
    exposedFilters?: ExposedFiltersType;
    visualFilters?: VisualFiltersData;
}

// The filters the backend exposes for the dominant leaf category, in the order the old exposed row showed them.
const EXPOSED_CATEGORY_SPECIFIC_KEYS = ['visual_filters', 'metadata_filters'] as const;

const buildCategorySpecific = (
    exposedFilters?: ExposedFiltersType,
    visualFilters?: VisualFiltersData
): CategoryExposure => {
    const filterId = [
        visualFilters?.visualFiltersList?.[0]?.id,
        ...EXPOSED_CATEGORY_SPECIFIC_KEYS.map((key) => exposedFilters?.[key]?.[0]?.id),
    ].find(Boolean);

    return filterId ? { mode: 'categorySpecific', filterId } : { mode: 'categorySpecific' };
};

const toCategoryOption = (child: Omit<CategoryOption, 'id'>): CategoryOption => ({
    ...child,
    id: child.params.nested_sub_category || child.params.sub_category || ALL_SUBCATEGORY,
});

const toAllCategoriesOption = ({ alias, count, selected, params }: SubCategoryFilter): CategoryOption => ({
    id: ALL_SUBCATEGORY,
    alias,
    count,
    selected,
    params,
});

/**
 * Decides which category controls the row exposes for a search query:
 * - "categories": the Categories dropdown, plus the top NSC pills. Used when the query has no dominant
 *   leaf category (DLC), or when the sub category of its DLC has NSCs with results. The DLC itself can be
 *   that sub category or one of its NSCs. With a DLC the dropdown lists only those NSCs, otherwise every
 *   related category.
 * - "categorySpecific": the first category specific dropdown (style, logo type...), plus its top values.
 *   Used when the sub category of the DLC has no NSCs with results. That dropdown is the first Visual filter,
 *   else the first visual or metadata filter the backend exposes.
 */
export const resolveCategoryExposure = ({
    subCategory,
    dominantLeafCategory,
    dominantLeafCategoryParent,
    exposedFilters,
    visualFilters,
}: ResolveCategoryExposureParams): CategoryExposure => {
    const categorySpecific = buildCategorySpecific(exposedFilters, visualFilters);
    const children = (subCategory?.children ?? []).filter(({ count }) => !!count);
    const debugInputs = {
        dominantLeafCategory,
        dominantLeafCategoryParent,
        hasSubCategory: !!subCategory,
        childrenTotal: subCategory?.children?.length ?? 0,
        childrenWithResults: children.map(({ alias, count, type, selected, params }) => ({
            alias,
            count,
            type,
            selected,
            params,
        })),
        categorySpecific,
    };

    if (!subCategory || children.length === 0) {
        debugCategoryExposure('step 1: no category children with results -> categorySpecific', debugInputs);

        return categorySpecific;
    }

    const dominantLeaf = String(dominantLeafCategory ?? '');
    const dominantParent = String(dominantLeafCategoryParent ?? '');

    const options = children.map(toCategoryOption);
    const nscOptions = options.filter(({ type }) => type === CATEGORY_TYPE.NESTED_SUB_CATEGORY);
    // Without a DLC any NSC can be offered; with one, only the NSCs of its own sub category, already merged on the server.
    const dominantNscOptions = (subCategory.nestedSubCategories ?? [])
        .filter(({ count }) => !!count)
        .map(toCategoryOption);
    const pillCandidates = dominantLeaf ? dominantNscOptions : nscOptions;

    if (dominantLeaf && pillCandidates.length === 0) {
        debugCategoryExposure('step 4: dominant sub category has no NSC candidates -> categorySpecific', {
            ...debugInputs,
            dominantLeaf,
            dominantParent,
            nestedSubCategoriesFromServer: subCategory.nestedSubCategories,
        });

        return categorySpecific;
    }

    debugCategoryExposure('step 5: categories', {
        ...debugInputs,
        dominantLeaf,
        dominantParent,
        pillCandidates: pillCandidates.map(({ alias, count }) => ({ alias, count })),
    });

    return {
        mode: 'categories',
        options: [toAllCategoriesOption(subCategory), ...(dominantLeaf ? dominantNscOptions : options)],
        topOptions: orderBy(pillCandidates, ({ count }) => count ?? 0, 'desc').slice(0, OPTION_PILLS_COUNT),
    };
};
